/**
 * Rich Educational SVG Typography with typewriter animation, chalk font, and math formatting.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface TextProps {
  text: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  fill?: string;
  align?: 'start' | 'middle' | 'end';
  maxWidth?: number;
  lineHeight?: number;
  letterSpacing?: number;
  typewriterProgress?: number; // 0 to 1
  isChalk?: boolean;
  shadow?: boolean;
}

export class TextObject extends SVGObject {
  public props: TextProps;
  private textElement: SVGTextElement | null = null;

  constructor(config: SVGObjectConfig & { props?: TextProps }) {
    super({ ...config, type: 'text' });
    this.props = {
      text: 'Text',
      fontSize: 28,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '600',
      fill: '#f8fafc',
      align: 'start',
      lineHeight: 1.4,
      typewriterProgress: 1,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const textEl = Renderer.createElement('text');

    const rawText = this.props.text || '';
    const count = Math.floor(rawText.length * (this.props.typewriterProgress ?? 1));
    const visibleText = rawText.slice(0, count);

    // Multiline splitting
    const lines = visibleText.split('\n');
    const fSize = this.props.fontSize || 28;
    const lHeight = (this.props.lineHeight && this.props.lineHeight > 5)
      ? this.props.lineHeight
      : fSize * (this.props.lineHeight || 1.4);

    Renderer.setAttributes(textEl, {
      x: 0,
      y: 0,
      'font-size': fSize,
      'font-family': this.props.isChalk ? '"Caveat", "Comic Sans MS", cursive' : this.props.fontFamily || 'Inter, sans-serif',
      'font-weight': this.props.fontWeight || '600',
      fill: this.props.fill || '#f8fafc',
      'text-anchor': this.props.align || 'start',
      'dominant-baseline': 'hanging'
    });

    lines.forEach((line, index) => {
      const tspan = Renderer.createElement('tspan');
      tspan.textContent = line || ' ';
      tspan.setAttribute('x', '0');
      tspan.setAttribute('dy', index === 0 ? '0' : `${lHeight}px`);
      textEl.appendChild(tspan);
    });

    this.textElement = textEl;
    this.container.appendChild(textEl);
  }

  public setText(text: string): void {
    this.props.text = text;
    this.render();
  }

  public setTypewriter(progress: number): void {
    this.props.typewriterProgress = Math.max(0, Math.min(1, progress));
    this.render();
  }
}
